<template>
  <div>
    <ValidationProvider
      v-slot="{ errors, classes }"
      :rules="criteriaValueValidations"
      slim
    >
      <input
        v-if="!isGeoTag && !allowBulkTagsUpload && !showPrepopulatedValues"
        v-model="criteriaValue"
        class="form-control filter-criteria-value"
        :class="classes"
        :placeholder="$t('ROUTER_VALUE_PH')"
        type="text"
      >
    </ValidationProvider>

    <ValidationProvider
      rules="required"
      slim
    >
      <MultiSelect
        v-if="isGeoTag && !showPrepopulatedValues"
        v-model="criteriaValue"
        class="filter-or-criteria__value"
        :list-items="uSStates"
        :class="{ 'multi-select-lg': isFullWidth }"
        :value-property="stateValueProperty"
        display-property="displayName"
        :search-placeholder="$t('ROUTER_TAG_SEARCH_STATES_PH')"
        :selected-placeholder="$t('ROUTER_TAG_SELECT_STATES_PH')"
        show-select-all
      />
    </ValidationProvider>

    <ValidationProvider
      rules="required"
      slim
    >
      <template v-if="showPrepopulatedValues && !allowBulkTagsUpload">
        <div v-if="showLoader">
          <Loader
            custom-class="size-sm"
          />

          {{ prepopulatedValuesOptions.loadingText }}
        </div>

        <SearchableDropdown
          v-if="!showLoader"
          v-model="criteriaValue"
          :config="prepopulatedDropdownOptions"
          :placeholder="prepopulatedValuesOptions.inputPlaceholder"
          rules="required"
          required
        />
      </template>
    </ValidationProvider>
    
    <BulkTagUploader
      v-if="allowBulkTagsUpload"
      v-model="criteriaValue"
      class="filter-or-criteria__value"
      :bulk-tag-list="bulkTagsUploads"
    />
  </div>
</template>

<script>
import messages from './locales';
import sharedMessages from '@/core/locales';
import BulkTagUploader from '@/core/components/BulkTagUploader/BulkTagUploader';
import MultiSelect from '@/core/components/MultiSelect/MultiSelect';
import { comparisonTypes, geoTagsIds } from '@/data/filterData';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import USStatesList from '@/data/USStatesList';
import { tagsValuesService, comparisonTypeService, utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapState } from 'vuex';
import { sortBy } from 'lodash';
import debounce from 'lodash/debounce';


export default {
  name: 'CriteriaValueInput',

  components: {
    BulkTagUploader, 
    MultiSelect,
    SearchableDropdown
  },

  props: {
    criteria: {
      type: Object,
      default: () => ({})
    },

    value: {
      type: [String, Array],
      default: ''
    },

    isFullWidth: {
      type: Boolean,
      default: false
    },

    bulkTagsUploads: {
      type: Array,
      default() {
        return [];
      }
    }
        
  },

  data() {
    return {
      uSStates: Object.freeze(USStatesList),

      prepopulatedValuesOptions: {},

      showLoader: false,

      prepopulatedValues: [],

      debouncedCriteriaValue: ''
    };
  },

  computed: {
    ...mapState({
      accountId: 'currentAccountId'
    }),

    criteriaValue: {
      get() { 
        return this.debouncedCriteriaValue; 
      },
      set(value) {
        this.debouncedCriteriaValue = value;
        this.debouncedUpdateValue(value);
        // this.handleCriteriaChange();
      }
    },

    allowBulkTagsUpload() {
      return comparisonTypes.some(c => { 
        return c.forBulkTags === true && c.value === this.criteria.comparisonTypeTemp.value;
      });
    },

    isGeoTag() {
      return !!geoTagsIds.find((tag) => {
        return tag.tagId === this.criteria.tagId;
      });
    },

    criteriaValueValidations() {
      let validations = 'required';

      if (this.criteria.comparisonTypeTemp.useNumericalValue) {
        validations += '|numeric';
      }

      return validations;
    },

    stateValueProperty() {
      if (geoTagsIds[this.criteria.tagId]) {
        return geoTagsIds[this.criteria.tagId].stateValueProperty;
      }
      
      return 'name';
    },

    showPrepopulatedValues() {
      return tagsValuesService.checkIfDropdownToBeShown(this.criteria.tagId) && comparisonTypeService.showDropdownValues(this.criteria.comparisonTypeTemp.key);
    },

    prepopulatedDropdownOptions() {
      return {
        options: this.prepopulatedValues,
        reduce: option => option[this.prepopulatedValuesOptions.valueProperty],
        placeholder: this.prepopulatedValuesOptions.inputPlaceholder,
        getOptionLabel: (option) => {
          return option && (this.prepopulatedValuesOptions.displayProperty ? option[this.prepopulatedValuesOptions.displayProperty] : option.name);
        },
        clearable: false
      };
    },
  },

  watch: {
    showPrepopulatedValues: {
      immediate: true,
      handler: async function() {
        await this.checkPrepopulatedFields();
      }
    },

    'criteria.tagId': {
      immediate: true,
      handler: async function() {
        await this.checkPrepopulatedFields();
      }
    },

    'criteria.value': {
      immediate: true,
      handler(newValue) {
        this.debouncedCriteriaValue = newValue;
      }
    }
  },

  methods: {
    debouncedUpdateValue: debounce(function(newValue) {
      this.$emit('input', newValue);
    }, 400),

    async checkPrepopulatedFields() {
      if (this.showPrepopulatedValues) {
        this.prepopulatedValuesOptions = tagsValuesService.getOptionsForDropdown(this.criteria.tagId);
        await this.loadPrepopulatedData();
      }
    },

    async loadPrepopulatedData() {
      const getDataFunction = tagsValuesService.getTagDataFunction(this.accountId, this.criteria.tagId);

      if (getDataFunction) {
        this.showLoader = true;

        try {
          const response = await getDataFunction();

          let tempList = response.data && response.data[this.prepopulatedValuesOptions.responseProperty];

          if (this.prepopulatedValuesOptions.filterProperty) {
            tempList = tempList.filter((item) => {
              return item && item[this.prepopulatedValuesOptions.filterProperty];
            });
          }

          this.prepopulatedValues = sortBy(tempList, [
            (o) => { 
              return this.prepopulatedValuesOptions.displayProperty ? o[this.prepopulatedValuesOptions.displayProperty] : o.name; }
          ]);
        } catch (error) {
          console.error(error);
          utilities.errorHandler(error);
        } finally {
          this.showLoader = false;
        }
        
      }
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style>

</style>